class VideoTestimonialsCarousel extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[data-slider]');
    this.slides = this.querySelectorAll('.video-testimonials__slide');
    this.prevButton = this.querySelector('.video-testimonials__nav-button--prev');
    this.nextButton = this.querySelector('.video-testimonials__nav-button--next');
    this.videos = this.querySelectorAll('video');
    
    this.currentIndex = 0;
    this.slideWidth = 0;
    this.isScrolling = false;
    
    this.init();
  }

  ensureVideoSource(video) {
    if (!video) return false;
    if (video.dataset.vtcLoadedSrc === 'true') return true;

    // Already has a source/currentSrc? Mark as loaded and continue.
    const existingSource = video.querySelector('source[src]');
    if (existingSource || video.currentSrc || video.src) {
      video.dataset.vtcLoadedSrc = 'true';
      return true;
    }

    const src = video.dataset.src;
    if (!src) return false;

    const sourceEl = document.createElement('source');
    sourceEl.src = src;
    sourceEl.type = 'video/mp4';
    video.appendChild(sourceEl);
    video.load();
    video.dataset.vtcLoadedSrc = 'true';
    return true;
  }

  init() {
    if (!this.slider || this.slides.length === 0) return;
    
    this.setupCarousel();
    this.setupVideoControls();
    this.setupEventListeners();
    this.updateNavigationButtons();
    
    // Initialize after a small delay to ensure proper rendering
    setTimeout(() => {
      this.updateSlideWidth();
    }, 100);
  }

  setupCarousel() {
    // Measure slide width first
    this.updateSlideWidth();

    // Prepare infinite loop: clone slides to both ends
    this.originalCount = this.slides.length;
    this.createInfiniteLoopClones();

    // Re-query slides after cloning
    this.slides = this.querySelectorAll('.video-testimonials__slide');
    // Re-query videos to include clones
    this.videos = this.querySelectorAll('video');

    // Set initial scroll position to the start of the original sequence
    this.contentWidthOriginal = this.originalCount * this.slideWidth;
    this.suspendSnap(() => { this.slider.scrollLeft = this.contentWidthOriginal; });

    // Add touch support for desktop mouse drag while preserving native touch scroll
    this.addTouchSupport();
  }

  setupVideoControls() {
    // Ensure we always bind on current videos (including clones)
    const videos = this.querySelectorAll('video');
    this.videos = videos;
    videos.forEach((video, index) => {
      if (video.hasAttribute('data-vtc-initialized')) return;
      video.setAttribute('data-vtc-initialized', 'true');
      const playButton = video.parentElement.querySelector('.video-testimonial__play-button');
      
      // Disable default video controls initially
      video.controls = false;
      
      if (playButton) {
        playButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.toggleVideo(video, playButton);
        });
      }

      // Handle video events
      video.addEventListener('play', () => {
        video.setAttribute('data-playing', 'true');
        video.controls = true; // Show controls when playing
        this.pauseOtherVideos(video);
        const poster = video.parentElement.querySelector('.video-testimonial__poster');
        if (poster) poster.classList.add('hidden');
        this.updateVideoInteractivity();
      });

      video.addEventListener('pause', () => {
        video.removeAttribute('data-playing');
        // Keep controls visible when paused
        const poster = video.parentElement.querySelector('.video-testimonial__poster');
        if (poster) poster.classList.remove('hidden');
        this.updateVideoInteractivity();
      });

      video.addEventListener('ended', () => {
        video.removeAttribute('data-playing');
        video.currentTime = 0;
        video.controls = false; // Hide controls when ended
        const poster = video.parentElement.querySelector('.video-testimonial__poster');
        if (poster) poster.classList.remove('hidden');
        this.updateVideoInteractivity();
      });

      // Handle click on video itself when not playing
      video.addEventListener('click', (e) => {
        if (video.paused) {
          e.preventDefault();
          this.ensureVideoSource(video);
          this.toggleVideo(video, playButton);
        }
      });
    });
    // Initial state
    this.updateVideoInteractivity();
  }

  toggleVideo(video, playButton) {
    const poster = video.parentElement.querySelector('.video-testimonial__poster');
    if (video.paused) {
      this.ensureVideoSource(video);
      const promise = video.play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch(() => {
          // If playback is blocked (e.g. iOS low power), show controls so user can start manually.
          video.controls = true;
        });
      }
      if (poster) poster.classList.add('hidden');
    } else {
      video.pause();
      if (poster) poster.classList.remove('hidden');
    }
  }

  pauseOtherVideos(currentVideo) {
    this.videos.forEach(video => {
      if (video !== currentVideo && !video.paused) {
        video.pause();
      }
    });
  }

  setupEventListeners() {
    // Navigation buttons
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.scrollToPrevious());
    }
    
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.scrollToNext());
    }

    // Wrap seamlessly when reaching edges (not debounced)
    this.slider.addEventListener('scroll', () => {
      this.handleInfiniteScrollWrap();
      this.updateVideoInteractivity();
    });

    // Scroll event for UI updates (debounced)
    this.slider.addEventListener('scroll', this.debounce(() => {
      this.updateNavigationButtons();
      this.updateCurrentIndex();
    }, 100));

    // Resize event for responsive behavior
    window.addEventListener('resize', this.debounce(() => {
      this.updateSlideWidth();
      this.updateNavigationButtons();
    }, 250));

    // Keyboard navigation
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.scrollToPrevious();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.scrollToNext();
      }
    });
  }

  updateVideoInteractivity() {
    if (!this.videos || this.videos.length === 0) return;
    const sliderRect = this.slider.getBoundingClientRect();
    const interactiveLeft = sliderRect.left;
    const interactiveRight = sliderRect.right;
    this.videos.forEach((video) => {
      const rect = video.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const isPlaying = !video.paused || video.hasAttribute('data-playing');
      const isVisible = centerX >= interactiveLeft && centerX <= interactiveRight;
      const enable = isPlaying || isVisible;
      video.style.pointerEvents = enable ? 'auto' : 'none';
      const poster = video.parentElement.querySelector('.video-testimonial__poster');
      if (poster) poster.style.pointerEvents = enable ? 'auto' : 'none';
    });
  }

  addTouchSupport() {
    let startX = 0;
    let scrollLeft = 0;
    let isDown = false;

    this.slider.addEventListener('mousedown', (e) => {
      isDown = true;
      startX = e.pageX - this.slider.offsetLeft;
      scrollLeft = this.slider.scrollLeft;
      this.slider.style.cursor = 'grabbing';
    });

    this.slider.addEventListener('mouseleave', () => {
      isDown = false;
      this.slider.style.cursor = 'grab';
    });

    this.slider.addEventListener('mouseup', () => {
      isDown = false;
      this.slider.style.cursor = 'grab';
    });

    this.slider.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - this.slider.offsetLeft;
      const walk = (x - startX) * 2;
      this.slider.scrollLeft = scrollLeft - walk;
    });

    // Touch events for mobile
  }

  createInfiniteLoopClones() {
    if (this.originalCount <= 0) return;

    const slidesArray = Array.from(this.slides);

    const prependFragment = document.createDocumentFragment();
    const appendFragment = document.createDocumentFragment();

    slidesArray.forEach((slide) => {
      appendFragment.appendChild(slide.cloneNode(true));
    });

    slidesArray.forEach((slide) => {
      prependFragment.appendChild(slide.cloneNode(true));
    });

    this.slider.insertBefore(prependFragment, this.slider.firstChild);
    this.slider.appendChild(appendFragment);
  }

  updateSlideWidth() {
    if (this.slides.length > 1) {
      const first = this.slides[0];
      const second = this.slides[1];
      const delta = second.offsetLeft - first.offsetLeft;
      if (delta > 0) {
        this.slideWidth = delta;
      } else {
        const slide = this.slides[0];
        const gap = parseFloat(window.getComputedStyle(this.slider).gap) || 0;
        this.slideWidth = slide.offsetWidth + gap;
      }
      if (this.originalCount && this.originalCount > 0) {
        const prevWidth = this.contentWidthOriginal || 0;
        this.contentWidthOriginal = this.originalCount * this.slideWidth;
        if (prevWidth && prevWidth !== this.contentWidthOriginal) {
          const current = this.slider.scrollLeft;
          const minPrev = prevWidth;
          const rel = ((current - minPrev) % prevWidth + prevWidth) % prevWidth;
          const minNew = this.contentWidthOriginal;
          this.suspendSnap(() => { this.slider.scrollLeft = minNew + rel; });
        }
      }
    }
  }

  updateCurrentIndex() {
    if (this.slideWidth > 0 && this.originalCount > 0) {
      const virtualIndex = Math.round(this.slider.scrollLeft / this.slideWidth);
      this.currentIndex = ((virtualIndex % this.originalCount) + this.originalCount) % this.originalCount;
    }
  }

  scrollToPrevious() {
    if (this.isScrolling) return;
    this.isScrolling = true;
    this.slider.scrollTo({ left: this.slider.scrollLeft - this.slideWidth, behavior: 'smooth' });
    setTimeout(() => { this.isScrolling = false; }, 400);
  }

  scrollToNext() {
    if (this.isScrolling) return;
    this.isScrolling = true;
    this.slider.scrollTo({ left: this.slider.scrollLeft + this.slideWidth, behavior: 'smooth' });
    setTimeout(() => { this.isScrolling = false; }, 400);
  }

  scrollToIndex(index) {
    if (this.slideWidth <= 0) return;
    const targetIndex = ((index % this.originalCount) + this.originalCount) % this.originalCount;
    const virtualIndex = Math.round(this.slider.scrollLeft / this.slideWidth);
    const currentBase = Math.floor(virtualIndex / this.originalCount) * this.originalCount;
    const desiredVirtualIndex = currentBase + targetIndex;
    const scrollPosition = desiredVirtualIndex * this.slideWidth;
    this.slider.scrollTo({ left: scrollPosition, behavior: 'smooth' });
    this.currentIndex = targetIndex;
  }

  updateNavigationButtons() {
    if (!this.prevButton || !this.nextButton) return;
    this.prevButton.disabled = false;
    this.nextButton.disabled = false;
    const isMobile = window.innerWidth < 990;
    if (isMobile) {
      this.prevButton.style.display = 'none';
      this.nextButton.style.display = 'none';
    } else {
      this.prevButton.style.display = 'flex';
      this.nextButton.style.display = 'flex';
    }
  }

  handleInfiniteScrollWrap() {
    if (!this.contentWidthOriginal || this.contentWidthOriginal <= 0) return;
    const min = this.contentWidthOriginal; // start of original sequence
    const max = this.contentWidthOriginal * 2; // end of original sequence
    const current = this.slider.scrollLeft;
    if (current < min || current >= max) {
      const width = this.contentWidthOriginal;
      const delta = ((current - min) % width + width) % width; // preserve offset within slide
      const target = min + delta;
      this.suspendSnap(() => { this.slider.scrollLeft = target; });
    }
  }

  suspendSnap(setter) {
    const prevSnap = this.slider.style.scrollSnapType;
    const prevBehavior = this.slider.style.scrollBehavior;
    this.slider.style.scrollSnapType = 'none';
    this.slider.style.scrollBehavior = 'auto';
    setter && setter();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.slider.style.scrollSnapType = prevSnap || '';
        this.slider.style.scrollBehavior = prevBehavior || '';
      });
    });
  }

  // Utility function to debounce events
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Custom element for individual video testimonials
class VideoTestimonial extends HTMLElement {
  constructor() {
    super();
    this.video = this.querySelector('video');
    this.playButton = this.querySelector('.video-testimonial__play-button');
    
    this.init();
  }

  init() {
    if (!this.video) return;
    
    // Set initial state
    this.video.removeAttribute('data-playing');
    
    // Intersection Observer disabled so video continues playing while scrolling carousel
  }

  // No intersection observer needed
  }

// Register custom elements
customElements.define('video-testimonials-carousel', VideoTestimonialsCarousel);
customElements.define('video-testimonial', VideoTestimonial);

// Initialize carousels when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Initialize any existing carousels
  const carousels = document.querySelectorAll('video-testimonials-carousel');
  carousels.forEach(carousel => {
    if (!carousel.hasAttribute('data-initialized')) {
      carousel.setAttribute('data-initialized', 'true');
    }
  });
});

// Handle dynamic content loading (for theme editor)
document.addEventListener('shopify:section:load', (event) => {
  const carousel = event.target.querySelector('video-testimonials-carousel');
  if (carousel) {
    // Re-initialize the carousel
    carousel.init();
  }
});

// Handle section unloading
document.addEventListener('shopify:section:unload', (event) => {
  const videos = event.target.querySelectorAll('video');
  videos.forEach(video => {
    video.pause();
    video.currentTime = 0;
  });
}); 